import React from 'react';

import { DataTable, Label, type TableColumn, ErrorMessage, Tooltip, Loading } from '@/src/ui';
import type { ExamHistoryExamTypeDTO, ExamHistoryStudentDTO } from '../models/examHistoryDTOs';
import { ReportCellStyle, ReportHeaderStyle, Constants } from '@/src/utils';

interface ExamHistoryTableProps {
    data: ExamHistoryExamTypeDTO[] | ExamHistoryStudentDTO[];
    viewMode: string;
    isLoading: boolean;
    error: string;
}

export default function ExamHistoryTable({
    data,
    viewMode,
    isLoading,
    error,
}: ExamHistoryTableProps) {
    const [hasSearched, setHasSearched] = React.useState(false);
    React.useEffect(() => {
        if (isLoading) setHasSearched(true);
    }, [isLoading]);
    if (isLoading) return <Loading message="Loading Exam History Report..." />;
    if (error) return <ErrorMessage error={error} />;
    if ((!data || !data.length) && hasSearched) return <div>No data available.</div>;
    if (!hasSearched) return null;

    const hasBenchmark = data.some((item) => item.examName.toLowerCase().includes('benchmark'));

    let columns: TableColumn[] = [];
    // Only one view mode now, but keeping both in case we want to bring back "by student" view later
    // if (viewMode === 'exam') {
    const BandBar: React.FC<{ bands: Record<string, number> }> = ({ bands }) => {
        const preferredOrder = ['Not Met', 'Below', 'Near', 'At/Above', 'Nearly', 'Met', 'Exceeds'];
        const keys = preferredOrder.filter((key) => key in bands);
        const bandKeys = keys.length > 0 ? keys : Object.keys(bands);
        const total = bandKeys.reduce((sum, key) => sum + (bands[key] ?? 0), 0);

        return (
            <div
                style={{
                    display: 'flex',
                    height: 18,
                    width: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: 'inset 0 0 2px #ccc',
                }}
            >
                {bandKeys.map((key) => {
                    const percent = total > 0 ? bands[key] : 0;
                    const nStudents = bands.totalStudents
                        ? Math.round((percent / 100) * bands.totalStudents)
                        : 0;
                    return (
                        <Tooltip
                            key={key}
                            tooltipText={`${key}: ${percent}% \n(${nStudents} students)`}
                            style={{ width: `${percent}%`, height: '100%' }}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    background: Constants.bandColors[key],
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </div>
        );
    };

    const bandNames = ['Below, Not Met', 'Near', 'At/Above, Met'];
    if (hasBenchmark) {
        bandNames.push('Exceeds');
    }

    function getBandValue(bands: Record<string, number>, bandNameList: string[]) {
        for (const bandName of bandNameList) {
            if (!isNaN(bands[bandName])) return bands[bandName];
        }
        return '';
    }

    const colorMap: Record<string, string> = Constants.bandColors;
    columns = [
        {
            dataKey: 'examName',
            header: 'Assessment',
            allowSort: true,
            align: 'left',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: ExamHistoryExamTypeDTO) => {
                return (
                    <a href={`/reports/questionList/${row.examId}`} target="_blank">
                        {row.examName}
                    </a>
                );
            },
        },
        {
            dataKey: 'studentsTested',
            header: 'Students Tested',
            allowSort: true,
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: ExamHistoryExamTypeDTO) => {
                if (row.studentsTested === null) return 'N/A';
                if (row.studentsTested === 0) return '0';
                return row.studentsTested;
            },
        },
        {
            dataKey: 'averageScore',
            header: 'Average Score',
            allowSort: true,
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: ExamHistoryExamTypeDTO) => {
                if (row.averageScore === 0) return '0';
                if (row.averageScore === null || row.averageScore === undefined) return 'N/A';
                return `${row.averageScore}%`;
            },
        },
        ...bandNames.map((band) => ({
            dataKey: band,
            header: band,
            align: 'center' as const,
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                let value;
                let color = '';
                const bands = row.bands;
                if (band === 'Below, Not Met') {
                    value = getBandValue(bands, ['Not Met', 'Below']);
                    color = 'Below';
                } else if (band === 'Near') {
                    value = getBandValue(bands, ['Near', 'Nearly']);
                    color = 'Near';
                } else if (band === 'At/Above, Met') {
                    value = getBandValue(bands, ['At/Above', 'Met']);
                    color = 'At/Above';
                } else if (band === 'Exceeds') {
                    value = getBandValue(bands, ['Exceeds']);
                    color = 'Exceeds';
                }

                if (!value && value !== 0) {
                    return <Label>–</Label>;
                }
                return <Label color={colorMap[color] || undefined}>{value}%</Label>;
            },
        })),
        {
            dataKey: 'BandBar',
            header: 'Band Distribution',
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: any) => {
                let bands: Record<string, number> = {};
                if (row.bands && typeof row.bands === 'object') {
                    bands = row.bands;
                    bands.totalStudents = row.studentsTested ?? 0;
                } else {
                    bands = {
                        'Not Met': row['Not Met'] ?? row['PercentBelow'] ?? 0,
                        Near: row['Near'] ?? row['PercentNear'] ?? 0,
                        'At/Above': row['At/Above'] ?? row['PercentAtAbove'] ?? 0,
                        totalStudents: row.studentsTested ?? 0,
                    };
                }
                return <BandBar bands={bands} />;
            },
        },
    ];
    // } else {
    // Hide old "by student" view mode but keep here in case
    // const colorMap: Record<string, string> = Constants.bandColors;
    // columns = [
    //     {
    //         dataKey: 'studentName',
    //         header: 'Student Name',
    //         allowSort: true,
    //         align: 'left',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //     },
    //     {
    //         dataKey: 'attemptNumber',
    //         header: 'Attempt #',
    //         allowSort: true,
    //         align: 'center',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //     },
    //     {
    //         dataKey: 'attemptDate',
    //         allowSort: true,
    //         header: 'Date',
    //         align: 'center',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //         formatter: (row: ExamHistoryStudentDTO) => {
    //             const value = row.attemptDate;
    //             if (!value) return '';
    //             const date = new Date(value);
    //             if (isNaN(date.getTime())) return value;
    //             return date.toLocaleDateString(undefined, {
    //                 year: 'numeric',
    //                 month: 'short',
    //                 day: 'numeric',
    //             });
    //         },
    //     },
    //     {
    //         dataKey: 'score',
    //         header: 'Score',
    //         allowSort: true,
    //         align: 'center',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //         formatter: (row: ExamHistoryStudentDTO) => {
    //             if (row.score === null || row.score === undefined) return 'N/A';
    //             if (row.score === 0) return '0';
    //             return `${row.score}%`;
    //         },
    //     },
    //     {
    //         dataKey: 'ReportingCategory',
    //         header: 'Band',
    //         allowSort: true,
    //         align: 'center',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //         formatter: (row: ExamHistoryStudentDTO) => {
    //             const bandValue = row.reportingCategory;
    //             return <Label color={colorMap[bandValue] || undefined}>{bandValue}</Label>;
    //         },
    //     },
    //     {
    //         dataKey: 'examName',
    //         allowSort: true,
    //         header: 'Assessment',
    //         align: 'left',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //     },
    //     {
    //         dataKey: 'examTypeName',
    //         header: 'Assessment Type',
    //         allowSort: true,
    //         align: 'center',
    //         emptyValue: '-',
    //         headerSx: ReportHeaderStyle,
    //         cellSx: ReportCellStyle,
    //     },
    // ];
    // }

    const defaultSortColumn = viewMode === 'exam' ? 'examName' : 'studentName';

    return (
        <DataTable
            columns={columns}
            data={data}
            // tableName={
            //     viewMode === 'exam' ? 'Exam History (By Assessment)' : 'Exam History (By Student)'
            // }
            tableName="Exam History"
            rowsPerPageInitial={50}
            enableExportCSV
            alternatingRowColor
            defaultSortColumn={defaultSortColumn}
            defaultSortDirection="asc"
        />
    );
}
